import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Input from "../../components/form/input/InputField";
import BookingsListCards from "../../components/booking/BookingsListCards";
import { formatMoney } from "../../utils/formatMoney";
import { formatUkDateTime } from "../../utils/dateUtils";
import { shortenVehicleTypeName } from "../../utils/vehicleTypeLabel";

interface BookingDirect {
  id: string;
  bookingId: number;
  pasengerName: string;
  pickupDateTime: Date | null;
  status: string;
  paymentStatus: string;
  amount: number | string | null;
  vehicleTypeName: string;
  bookingSource: string | null;
}

interface SortConfig {
  key: keyof BookingDirect | '';
  direction: 'asc' | 'desc';
}

const BOOKING_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "Paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
] as const;

const PAYMENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
] as const;

/** Numeric booking ID when set; otherwise first 8 chars of UUID (e.g. 948F62A1). */
function bookingReference(booking: BookingDirect): string {
  if (booking.bookingId > 0) return String(booking.bookingId);
  return booking.id.slice(0, 8).toUpperCase();
}

function statusSelectClass(status: string): string {
  const base =
    "h-8 max-w-[9.5rem] cursor-pointer rounded-full border-0 px-2.5 text-xs font-semibold appearance-auto focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:cursor-wait disabled:opacity-60";
  switch (status.toLowerCase()) {
    case "completed":
    case "paid":
      return `${base} bg-green-100 text-green-800`;
    case "pending":
      return `${base} bg-yellow-100 text-yellow-800`;
    case "cancelled":
    case "failed":
      return `${base} bg-red-100 text-red-800`;
    case "confirmed":
    case "assigned":
    case "in_progress":
    case "refunded":
      return `${base} bg-blue-100 text-blue-800`;
    default:
      return `${base} bg-gray-100 text-gray-800`;
  }
}

function ensureOption(
  options: readonly { value: string; label: string }[],
  current: string,
): { value: string; label: string }[] {
  if (!current) return [...options];
  if (options.some((o) => o.value === current || o.value.toLowerCase() === current.toLowerCase())) {
    return [...options];
  }
  return [{ value: current, label: current }, ...options];
}

function matchedOptionValue(
  options: readonly { value: string; label: string }[],
  current: string,
  fallback: string,
): string {
  if (!current) return fallback;
  const exact = options.find((o) => o.value === current);
  if (exact) return exact.value;
  const ci = options.find((o) => o.value.toLowerCase() === current.toLowerCase());
  if (ci) return ci.value;
  return current;
}

const STATS_PERIODS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "month", label: "This month" },
  { value: "all", label: "All time" },
] as const;

type StatsPeriod = (typeof STATS_PERIODS)[number]["value"];

interface BookingStats {
  period: string;
  periodLabel: string;
  from: string | null;
  to: string | null;
  totalBookings: number;
  completed: number;
  confirmed: number;
  pending: number;
  cancelled: number;
  paid: number;
  unpaid: number;
  totalRevenue: number;
  paidRevenue: number;
  avgBookingValue: number;
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-white/90">
        {value}
      </p>
      {sub ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</p>
      ) : null}
    </div>
  );
}

export default function BookingsList() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingDirect[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [updatingPaymentId, setUpdatingPaymentId] = useState<string | null>(null);
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>("30d");
  const [stats, setStats] = useState<BookingStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/direct-bookings");
      const allBookings = response.data;
      
      const filteredBookings = allBookings.filter((booking: BookingDirect) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          booking.pasengerName?.toLowerCase().includes(searchLower) ||
          bookingReference(booking).toLowerCase().includes(searchLower) ||
          booking.id?.toLowerCase().includes(searchLower) ||
          booking.vehicleTypeName?.toLowerCase().includes(searchLower) ||
          booking.bookingSource?.toLowerCase().includes(searchLower)
        );
      });

      if (sortConfig.key !== '') {
        filteredBookings.sort((a: BookingDirect, b: BookingDirect) => {
          const aValue = String(a[sortConfig.key as keyof BookingDirect] || '');
          const bValue = String(b[sortConfig.key as keyof BookingDirect] || '');
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      const totalItems = filteredBookings.length;
      const calculatedTotalPages = Math.ceil(totalItems / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      setTotalPages(calculatedTotalPages);
      setBookings(filteredBookings.slice(startIndex, endIndex));
    } catch (error) {
      setError("Failed to fetch bookings");
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage, pageSize, sortConfig]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBookings();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchBookings]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await axiosInstance.get(
        `/direct-bookings/stats?period=${encodeURIComponent(statsPeriod)}`,
      );
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching booking stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [statsPeriod]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  const handleSort = (key: keyof BookingDirect) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (key: keyof BookingDirect) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === i
              ? "rounded-lg bg-brand-500 text-white"
              : "rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const patchBookingLocal = (id: string, patch: Partial<BookingDirect>) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    );
  };

  const handleStatusChange = async (booking: BookingDirect, status: string) => {
    if (status === booking.status) return;
    const previous = booking.status;
    patchBookingLocal(booking.id, { status });
    setUpdatingStatusId(booking.id);
    setError("");
    try {
      await axiosInstance.put(`/direct-bookings/${booking.id}/status`, { status });
      void fetchStats();
    } catch (err) {
      console.error("Error updating booking status:", err);
      patchBookingLocal(booking.id, { status: previous });
      setError("Failed to update booking status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handlePaymentStatusChange = async (
    booking: BookingDirect,
    paymentStatus: string,
  ) => {
    if (paymentStatus === booking.paymentStatus) return;
    const previous = booking.paymentStatus;
    patchBookingLocal(booking.id, { paymentStatus });
    setUpdatingPaymentId(booking.id);
    setError("");
    try {
      await axiosInstance.put(`/direct-bookings/${booking.id}/payment-status`, {
        paymentStatus,
      });
      void fetchStats();
    } catch (err) {
      console.error("Error updating payment status:", err);
      patchBookingLocal(booking.id, { paymentStatus: previous });
      setError("Failed to update payment status");
    } finally {
      setUpdatingPaymentId(null);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return formatUkDateTime(date);
  };

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Booking Log"  />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label
            htmlFor="booking-stats-period"
            className="text-sm font-medium text-gray-600 dark:text-gray-300"
          >
            Stats period
          </label>
          <select
            id="booking-stats-period"
            value={statsPeriod}
            onChange={(e) => setStatsPeriod(e.target.value as StatsPeriod)}
            className="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
          >
            {STATS_PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          {stats?.periodLabel ? (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {stats.periodLabel}
            </span>
          ) : null}
        </div>
        <Link
          to="/bookings/add"
          className="inline-flex h-10 items-center justify-center rounded-lg bg-brand-500 px-4 text-sm font-medium leading-none text-white transition-colors hover:bg-brand-600"
        >
          Add Booking
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Bookings"
          value={statsLoading ? "…" : stats?.totalBookings ?? 0}
          sub="Created in period"
        />
        <StatCard
          label="Revenue"
          value={
            statsLoading
              ? "…"
              : formatMoney(stats?.totalRevenue ?? 0)
          }
          sub={
            stats
              ? `Avg ${formatMoney(stats.avgBookingValue)}`
              : undefined
          }
        />
        <StatCard
          label="Paid"
          value={statsLoading ? "…" : stats?.paid ?? 0}
          sub={
            stats ? `${formatMoney(stats.paidRevenue)} collected` : undefined
          }
        />
        <StatCard
          label="Completed"
          value={statsLoading ? "…" : stats?.completed ?? 0}
        />
        <StatCard
          label="Active"
          value={
            statsLoading
              ? "…"
              : (stats?.confirmed ?? 0) + (stats?.pending ?? 0)
          }
          sub={
            stats
              ? `${stats.pending} pending · ${stats.confirmed} confirmed`
              : undefined
          }
        />
        <StatCard
          label="Cancelled"
          value={statsLoading ? "…" : stats?.cancelled ?? 0}
        />
      </div>

      <div className="space-y-3 lg:space-y-0 lg:overflow-hidden lg:rounded-2xl lg:border lg:border-gray-200 lg:bg-white lg:shadow-theme-sm dark:lg:border-gray-800 dark:lg:bg-white/[0.03]">
        <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:rounded-none lg:border-0 lg:border-b lg:shadow-none lg:p-4 dark:lg:border-gray-800">
          <Input
            placeholder="Search bookings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        {error && (
          <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400 lg:rounded-none lg:border-x-0 lg:border-t-0">
            {error}
          </div>
        )}

        <BookingsListCards
          bookings={bookings}
          loading={loading}
          onOpen={(id) => navigate(`/bookings/${id}/view`)}
          onStatusChange={handleStatusChange}
          onPaymentStatusChange={handlePaymentStatusChange}
          updatingStatusId={updatingStatusId}
          updatingPaymentId={updatingPaymentId}
        />

        <div className="hidden overflow-x-auto lg:block">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-white/[0.02]">
              <tr>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('bookingId')}
                >
                  Booking No{renderSortIcon('bookingId')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('pasengerName')}
                >
                  Passenger{renderSortIcon('pasengerName')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('pickupDateTime')}
                >
                  Pickup Time{renderSortIcon('pickupDateTime')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('vehicleTypeName')}
                >
                  Vehicle Type{renderSortIcon('vehicleTypeName')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('amount')}
                >
                  Amount{renderSortIcon('amount')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  Status{renderSortIcon('status')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('bookingSource')}
                >
                  Website{renderSortIcon('bookingSource')}
                </th>
                <th
                  className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 cursor-pointer"
                  onClick={() => handleSort('paymentStatus')}
                >
                  Payment{renderSortIcon('paymentStatus')}
                </th>
                <th className="px-5 py-3 text-start text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                    </div>
                  </td>
                </tr>
              ) : bookings.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No bookings found
                  </td>
                </tr>
              ) : (
                bookings.map((booking) => (
                  <tr
                    key={booking.id}
                    onClick={() => navigate(`/bookings/${booking.id}/view`)}
                    className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                  >
                    <td className="px-5 py-4 text-sm font-mono text-gray-900 dark:text-white/90">
                      {bookingReference(booking)}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {booking.pasengerName}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {formatDate(booking.pickupDateTime)}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90 whitespace-nowrap">
                      {shortenVehicleTypeName(booking.vehicleTypeName)}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {formatMoney(booking.amount)}
                    </td>
                    <td
                      className="px-5 py-4 text-sm text-gray-900 dark:text-white/90"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <select
                        value={matchedOptionValue(
                          BOOKING_STATUS_OPTIONS,
                          booking.status,
                          "pending",
                        )}
                        disabled={updatingStatusId === booking.id}
                        onChange={(e) =>
                          void handleStatusChange(booking, e.target.value)
                        }
                        className={statusSelectClass(booking.status || "pending")}
                        aria-label={`Booking status for ${bookingReference(booking)}`}
                      >
                        {ensureOption(BOOKING_STATUS_OPTIONS, booking.status).map(
                          (opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ),
                        )}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90 text-sm text-gray-600">
                      {booking.bookingSource || '—'}
                    </td>
                    <td
                      className="px-5 py-4 text-sm text-gray-900 dark:text-white/90"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <select
                        value={matchedOptionValue(
                          PAYMENT_STATUS_OPTIONS,
                          booking.paymentStatus,
                          "pending",
                        )}
                        disabled={updatingPaymentId === booking.id}
                        onChange={(e) =>
                          void handlePaymentStatusChange(booking, e.target.value)
                        }
                        className={statusSelectClass(
                          booking.paymentStatus || "pending",
                        )}
                        aria-label={`Payment status for ${bookingReference(booking)}`}
                      >
                        {ensureOption(
                          PAYMENT_STATUS_OPTIONS,
                          booking.paymentStatus,
                        ).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td
                      className="px-5 py-4 text-sm font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-3">
                        <Link
                          to={`/bookings/${booking.id}/view`}
                          className="px-3 py-1 text-blue-600 hover:text-blue-800"
                        >
                          View
                        </Link>
                        <Link
                          to={`/bookings/edit/${booking.id}`}
                          className="px-3 py-1 text-green-600 hover:text-green-800"
                        >
                          Edit
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && bookings.length > 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white px-3 py-4 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] sm:px-6 lg:rounded-none lg:border-0 lg:border-t lg:shadow-none dark:lg:border-gray-800">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm dark:border-gray-700 dark:bg-gray-900 sm:h-auto sm:w-auto sm:rounded sm:px-2 sm:py-1"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              <div className="flex flex-wrap justify-center gap-1 sm:justify-end">
                {renderPagination()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
